import { useState } from 'react';
import Modal from '../Modal';
import Button from '../Button';
import Alert from '../Alert';
import { transferEnterpriseClassMembers } from '../../api/admin';

/**
 * 「转走成员」弹窗。多选源班成员（后端 members 已排软删）+ 选目标班（活跃且≠源）→ transfer。
 *
 * Props:
 *   entId         企业 id
 *   sourceClass   源班行 { id, name, members: [{id, display_name}] }
 *   activeClasses 当前全部活跃班（目标下拉，内部排除源班）
 *   onClose       关闭
 *   onDone        转走成功回调（父级据此刷新本区）
 */
export default function TransferMembersModal({ entId, sourceClass, activeClasses, onClose, onDone }) {
  const members = sourceClass?.members || [];
  const targets = (activeClasses || []).filter((c) => c.id !== sourceClass?.id);

  const [selected, setSelected] = useState([]);
  const [toId, setToId] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const toggle = (uid) =>
    setSelected((cur) => (cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid]));
  const toggleAll = () =>
    setSelected((cur) => (cur.length === members.length ? [] : members.map((m) => m.id)));

  const handleSubmit = async () => {
    if (selected.length === 0) { setError('请至少选择一名成员'); return; }
    if (!toId) { setError('请选择目标班级'); return; }
    setSaving(true); setError('');
    try {
      await transferEnterpriseClassMembers(entId, sourceClass.id, {
        to_id: Number(toId),
        user_ids: selected,
      });
      onDone?.();
    } catch (e) {
      setError(e.response?.data?.detail || '转走成员失败');
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`转走成员：${sourceClass?.name || ''}`}
      maxWidth={520}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>取消</Button>
          <Button size="sm" onClick={handleSubmit} loading={saving}>转走</Button>
        </>
      }
    >
      {error && <Alert type="error">{error}</Alert>}

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>目标班级（仅活跃班级）</label>
        <select value={toId} onChange={(e) => setToId(e.target.value)} style={selectStyle}>
          <option value="">请选择目标班级</option>
          {targets.map((c) => (
            <option key={c.id} value={c.id}>{c.registration_code} · {c.name}</option>
          ))}
        </select>
        {targets.length === 0 && (
          <div style={hintStyle}>暂无其它活跃班级可转入，请先新建一个班级。</div>
        )}
      </div>

      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <label style={{ ...labelStyle, marginBottom: 0 }}>
            选择要转走的成员（{selected.length}/{members.length}）
          </label>
          {members.length > 0 && (
            <button type="button" onClick={toggleAll} style={linkBtnStyle}>
              {selected.length === members.length ? '取消全选' : '全选'}
            </button>
          )}
        </div>
        {members.length === 0 ? (
          <div style={hintStyle}>该班级暂无成员。</div>
        ) : (
          <div style={listStyle}>
            {members.map((m) => (
              <label key={m.id} style={rowStyle}>
                <input type="checkbox" checked={selected.includes(m.id)} onChange={() => toggle(m.id)} />
                <span style={{ fontSize: 13, color: '#374151' }}>{m.display_name}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

const labelStyle = { fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 };
const selectStyle = { padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, width: '100%', boxSizing: 'border-box', background: '#fff' };
const hintStyle = { fontSize: 12, color: '#94a3b8', marginTop: 6 };
const linkBtnStyle = { background: 'none', border: 'none', color: '#2563eb', fontSize: 12, cursor: 'pointer', padding: 0 };
const listStyle = { display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 240, overflowY: 'auto', padding: '8px 12px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' };
const rowStyle = { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '4px 0' };
